import {
  Box,
  createShapeId,
  getIndexAbove,
  getIndexBetween,
  sortByIndex,
  TLPageId,
  TLParentId,
  TLShape,
  TLShapeId,
  useEditor,
  useValue,
} from "tldraw"
import { PageScroller } from "./PageScroller"
import { ToolsToolbar } from "./ToolsToolbar"
import { useCallback, useEffect, useMemo, useState } from "react"
import { ToolbarPosition } from "./ToolbarPosition"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { PageSettingsDialog2 } from "./PageSettingsDialog2"
import {
  hidePageSettingsDialog,
  setEditingLayer,
} from "../features/app/appSlice"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { SelectionToolbar } from "./SelectionToolbar"
import { StickerParametersToolbar } from "./StickerParametersToolbar"
import { StickerShape } from "../../shared/StickerShape"
import { LDPage } from "../../shared/LDPage"
import { defaultTransformation } from "./TransformationButton"
import Dialog from "@mui/material/Dialog"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { CustomDrawShape } from "../../shared/CustomDrawShape"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import { LayersToolbar2 } from "./LayersToolbar2"
import { LDDocument, LDDocumentMeta } from "../../shared/LDDocument"
import { Color, Expr } from "../../shared/Expr"
import { ColorToolsToolbar } from "./ColorToolsToolbar"
import { Plus, Redo, Undo } from "lucide-react"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { ToolButton } from "./ToolButton"
import { RoundedToolbar } from "./RoundedToolbar"
import { last } from "lodash"
import { LayerOverridesToolbar } from "./LayerOverridesToolbar"

type Props = {
  fillColor: Color
  strokeColor: Color
  markerRadius: number
  strokeWidth: number
  showLayers: boolean
  showGrid: boolean
  onFillColorChange: (c: Color) => void
  onStrokeColorChange: (c: Color) => void
  onMarkerRadiusChange: (radius: number) => void
  onPencilRadiusChange: (radius: number) => void
  onShowGridChange: (showGrid: boolean) => void
  onShowLayersChange: (showLayers: boolean) => void
  onOnlySelectedLayerChange: (onlySelectedLayer: boolean) => void
}

export function InnerCanvas({
  fillColor,
  strokeColor,
  markerRadius,
  showLayers,
  showGrid,
  strokeWidth,
  onFillColorChange,
  onStrokeColorChange,
  onMarkerRadiusChange,
  onShowGridChange,
  onShowLayersChange,
  onPencilRadiusChange,
  onOnlySelectedLayerChange,
}: Props) {
  const editor = useEditor()

  const page = useCurrentPage()

  // hide the layer settings dialog when interacting with the canvas
  editor.on("event", (event) => {
    if (event.name === "pointer_down") {
      if (editingLayer) {
        dispatch(setEditingLayer(false))
      }
    }
  })

  // This is whether the animation is playing
  const [isGoing, setIsGoing] = useState(false)

  // Add back in for timer to work
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (isGoing) {
  //       editor.updatePage({
  //         ...page,
  //         meta: {
  //           ...page.meta,
  //           parameterValues: {
  //             ...page.meta.parameterValues,
  //             time: Expr.lit(Date.now()),
  //           },
  //         },
  //       })
  //     }
  //   }, 10) // 100ms = 10 times per second

  //   return () => clearInterval(interval)
  // }, [editor, page, isGoing])

  const pageSettingsDialogOpen = useAppSelector(
    (state) => state.app.pageSettingsDialogOpen,
  )

  const editingLayer = useAppSelector((state) => state.app.editingLayer)

  const dispatch = useAppDispatch()

  const instanceMeta = useValue(
    "instance meta",
    () => editor.getInstanceState().meta as LDInstanceMeta,
    [editor],
  ) as LDInstanceMeta

  function handleSelectLayer(layerId: string) {
    editor.run(function () {
      //console.log({ instanceMeta })
      if (instanceMeta.onlySelectedLayer) {
        const layerShapes = editor
          .getCurrentPageShapes()
          .filter(
            (shape) =>
              LDShape.getLayerId(shape as LDShape) === layerId &&
              shape.parentId === page.id,
          )
          .map((shape) => ({ ...shape, isLocked: false }))
        editor.updateShapes(layerShapes)
        const nonLayerShapes = editor
          .getCurrentPageShapes()
          .filter(
            (shape) =>
              LDShape.getLayerId(shape as LDShape) !== layerId &&
              shape.parentId === page.id,
          )
          .map((shape) => ({ ...shape, isLocked: true }))
        editor.updateShapes(nonLayerShapes)
      }
      editor.updatePage({
        id: page.id,
        meta: { ...page.meta, currentLayer: layerId },
      })
    })
    setEditingLayer(false)
  }

  function handleStickerDrag(
    pageId: TLPageId,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    editor.markHistoryStoppingPoint()
    setEditingLayer(false)

    editor.setCurrentTool("select")

    const shapeId = createShapeId() // Generate a unique ID for the shape

    // Convert local coordinates to global coordinates
    const { x: scrollX, y: scrollY, z: zoom } = editor.getCamera()

    const globalX = x - scrollX
    const globalY = y - scrollY

    const targetPage = editor.getPage(pageId) as LDPage

    const layersArray = Object.values(page.meta.layers).sort()

    const currentLayer = page.meta.currentLayer

    const currentLayerIndexInList = layersArray.findIndex(
      (l) => l.id === currentLayer,
    )
    //console.log("currentLayerIndex", layersArray[currentLayerIndexInList].index)
    const layerAbove = layersArray[currentLayerIndexInList + 1] ?? null
    //console.log("layerAbove", layerAbove)

    const highestShapeIndexOnCurrentLayer =
      editor
        .getCurrentPageShapes()
        .filter((s) => LDShape.getLayerId(s as LDShape) === currentLayer)
        .sort((a, b) => b.index.localeCompare(a.index))[0]?.index ??
      layersArray[currentLayerIndexInList].index

    const nextLayerIndex =
      layerAbove?.index ?? getIndexAbove(highestShapeIndexOnCurrentLayer)

    const index = getIndexBetween(
      highestShapeIndexOnCurrentLayer,
      nextLayerIndex,
    )

    const shapeIds = editor.getPageShapeIds(pageId)

    let commonBounds: Box | undefined
    ;[...shapeIds]
      .filter((id) => {
        const shape = editor.getShape(id)!
        return true //shape.meta.pageId === pageId || shape.props.pageId === pageId
      })
      .forEach((shapeId) => {
        const bounds = editor.getShapeMaskedPageBounds(shapeId)
        if (!bounds) return
        if (!commonBounds) {
          commonBounds = bounds.clone()
        } else {
          commonBounds = commonBounds.expand(bounds)
        }
      })

    const newShapes = [...shapeIds]
      .map((id) => editor.getShape(id))
      .filter((shape) => !(shape as CustomDrawShape)?.props?.isClone)
      .map((shape) => {
        const newId = shape?.id + shapeId

        const shapeOffsetX = shape!.x - commonBounds!.x
        const shapeOffsetY = shape!.y - commonBounds!.y
        return {
          ...(shape as TLShape),
          id: newId as TLShapeId,
          x:
            shape!.type !== "custom-group"
              ? shapeOffsetX + globalX // - shapeOffsetX
              : shape!.x,
          y:
            shape!.type !== "custom-group"
              ? shapeOffsetY + globalY //- shapeOffsetY
              : shape!.y,
        }
      })

    editor.createShapes(newShapes)

    editor.createShapes<LDGroupShape>([
      {
        id: shapeId,
        type: "custom-group",
        parentId: editor.getCurrentPageId(),
        index,
        x: globalX,
        y: globalY,
        opacity: 1,
        props: {
          layer: (editor.getCurrentPage() as LDPage).meta.currentLayer,
          pageId: pageId,
          parameterValues: targetPage.meta.parameterValues,
        },
      },
    ])

    // Should reparent only top level shapes but copy all shapes
    const topLevelShapes = newShapes.filter((s) => s.parentId === pageId)
    editor.reparentShapes(topLevelShapes, shapeId)

    newShapes
      .filter((s) => s.parentId !== pageId)
      .forEach((shape) => {
        editor.updateShape({
          ...shape,
          parentId: (shape.parentId + shapeId) as TLParentId,
        })
      })

    const createdShape = editor.getShape(shapeId)
    //("Created shape:", createdShape)

    if (createdShape) {
      editor.dispatch({
        type: "pointer",
        target: "shape",
        shape: createdShape, // The shape to drag
        point: { x: x + 30, y: y + 70 }, // The point to drag the shape to
        name: "pointer_down",
        shiftKey: false,
        altKey: false,
        ctrlKey: false,
        metaKey: false,
        accelKey: false,
        button: 0,
        isPen: editor.inputs.isPen,
        pointerId: 1,
      })
    }
  }

  const isSelecting = useValue(
    "isSelecting",
    () =>
      editor.getCurrentToolId() === "select" &&
      editor.getSelectedShapeIds().length > 0,
    [editor],
  )

  const selectedShapeIds = useValue(
    "selected shape ids",
    () => editor.getSelectedShapeIds(),
    [editor],
  )

  const hoveredShapeId = useValue(
    "hovered shape id",
    () => editor.getHoveredShapeId(),
    [editor],
  )

  useEffect(() => {
    if (hoveredShapeId) {
      let shape = editor.getShape(hoveredShapeId) as TLShape
      while (shape.parentId !== editor.getCurrentPageId()) {
        const parentId = shape.parentId
        shape = editor.getShape(parentId) as TLShape
      }
      editor.setHoveredShape(shape.id)
    }
  }, [hoveredShapeId, editor])

  useEffect(() => {
    if (isSelecting) {
      const selectedShapes = editor.getSelectedShapes()

      const newSelectedShapes2 = selectedShapes.map((lowShape) => {
        let shape = lowShape
        let parentId = shape.parentId
        while (parentId !== editor.getCurrentPageId()) {
          shape = editor.getShape(parentId) as TLShape
          parentId = shape.parentId
        }
        return shape
      })

      const newSelectedShapeIds = newSelectedShapes2.map((shape) => shape.id)
      // const newSelectedShapes = selectedShapes.concat(
      //   selectedShapes.flatMap((shape) =>
      //     editor
      //       .getBindingsToShape(shape, "fill")
      //       .map((binding) => {
      //         return binding.fromId
      //       })
      //       .map((id) => editor.getShape(id) as CustomDrawShape),
      //   ),
      // )
      const newSelectedShapes: TLShapeId[] = newSelectedShapeIds.concat(
        newSelectedShapeIds.flatMap((shapeId) => {
          const shape = editor.getShape(shapeId) as CustomDrawShape
          return editor
            .getBindingsToShape(shape, "fill")
            .map((binding) => {
              return binding.fromId
            })

            .concat(
              editor
                .getBindingsFromShape(shape, "fill")
                .map((binding) => binding.toId),
            )
        }),
      )
      const filtered = newSelectedShapes.filter((item, index, self) => {
        return self.indexOf(item) === index
      })
      editor.selectNone()
      editor.setSelectedShapes(filtered)
    }
  }, [isSelecting, selectedShapeIds[0], selectedShapeIds[1]])

  const selectedShapes = useValue(
    "selected shapes",
    () => {
      return editor.getSelectedShapes() as (CustomDrawShape | LDGroupShape)[]
    },
    [editor],
  )

  const selectedSticker = useValue(
    "selected sticker",
    () => {
      const shape = editor.getOnlySelectedShape()

      if (
        !shape ||
        !(shape.type === "custom-group" || shape.type === "custom-draw")
      )
        return null

      return shape as CustomDrawShape | LDGroupShape
    },
    [editor],
  )

  // const parameters = useValue(
  //   "sticker parameters",
  //   () => {
  //     if (selectedSticker) {
  //       const stickerPage = editor.getPage(
  //         selectedSticker.meta.pageId,
  //       ) as LDPage
  //       const stickerOverrides = selectedSticker.meta.parameterValues
  //       return Object.fromEntries(
  //         Object.entries(stickerPage.meta.parameters).map(([id, p]) => {
  //           return [
  //             id,
  //             {
  //               name: p.name,
  //               type: p.type,
  //               value: stickerOverrides?.[id] ?? null,
  //               defaultValue: p.defaultValue,
  //             },
  //           ] as const
  //         }),
  //       )
  //     }
  //     return {}
  //   },
  //   [selectedSticker, editor],
  // )

  const doc = useValue(
    "document",
    () => editor.getDocumentSettings() as LDDocument,
    [editor],
  )

  const recentFillColors = useValue(
    "recent fill colors",
    () => doc.meta.recentFillColors ?? [],
    [doc],
  )
  const recentStrokeColors = useValue(
    "recent stroke colors",
    () => doc.meta.recentStrokeColors ?? [],
    [doc],
  )

  const sortedLayers = [...Object.values(page.meta.layers)].sort(sortByIndex)

  const handleAddLayerClick = useCallback(
    function handleAddLayerClick() {
      editor.run(function () {
        editor.markHistoryStoppingPoint()

        const newLayerId = `layer-${sortedLayers.length}`

        const newLayer = {
          ...page.meta.layers[page.meta.currentLayer],
          index: getIndexAbove(last(sortedLayers)!.index),
          name: `Layer ${sortedLayers.length + 1}`,
          id: newLayerId,
          layerOverrides: {
            strokeWidth: null,
            strokeColor: null,
            fillColor: null,
          },
        }
        // First update the page with the new layer
        editor.updatePage({
          id: page.id,

          meta: {
            ...page.meta,
            layers: {
              ...page.meta.layers,
              [newLayerId]: newLayer,
            },
            currentLayer: newLayerId,
          },
        })

        // Then optionally add the color parameter if it's a fill

        close()
      })
    },
    [editor, sortedLayers, page],
  )

  // selectedShapes.length > 0 &&
  //   console.log({ page: editor.getPage(selectedShapes[0]?.props.pageId) })
  return (
    <>
      <div style={{ position: "absolute", top: 18, left: 20 }}>
        {/* <button onClick={() => setIsGoing(!isGoing)}>start/stop</button> */}
        <PageScroller
          onStickerDrag={(pageId, x, y, width, height) =>
            handleStickerDrag(pageId, x, y, width, height)
          }
        />
      </div>

      <ToolbarPosition visible anchor="right" style={{ top: 30, right: 16 }}>
        <div
          style={{
            display: "flex",
            borderRadius: "999px",
            color: "white",
            boxShadow: "1px 1px 3px rgba(0,0,0,0.2)",
            background: "white",
            padding: "2px",
            gap: 2,
          }}
        >
          <ToolButton
            icon="layers"
            tooltip="Layers"
            tooltipPlacement="bottom"
            variant="toggle"
            selected={showLayers}
            onClick={() => onShowLayersChange(!showLayers)}
          ></ToolButton>
          <ToolButton
            icon="grid"
            tooltip="Toggle grid"
            tooltipPlacement="bottom"
            variant="toggle"
            selected={showGrid}
            onClick={() => onShowGridChange(!showGrid)}
          ></ToolButton>
          <IconButton
            onClick={() => editor.undo()}
            disabled={!editor.getCanUndo()}
          >
            <Tooltip title="Undo">
              <Undo size={20} />
            </Tooltip>
          </IconButton>
          <IconButton
            onClick={() => editor.redo()}
            disabled={!editor.getCanRedo()}
          >
            <Tooltip title="Undo">
              <Redo size={20} />
            </Tooltip>
          </IconButton>
        </div>
      </ToolbarPosition>

      <ToolbarPosition
        visible={!isSelecting}
        anchor="right"
        style={{ top: 96, right: 16 }}
      >
        <ColorToolsToolbar
          recentFillColors={recentFillColors}
          recentStrokeColors={recentStrokeColors}
          fillColor={fillColor}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          markerRadius={markerRadius}
          onFillColorChange={(color) => {
            // notify parent
            onFillColorChange(color)

            // add to the recent colors
            editor.updateDocumentSettings({
              meta: {
                ...doc.meta,
                recentFillColors: [
                  color,
                  ...recentFillColors.filter((c) => c !== color).slice(0, 10),
                ],
              } as LDDocumentMeta,
            })
          }}
          onStrokeColorChange={(color) => {
            // notify parent
            onStrokeColorChange(color)

            // add to the recent colors
            editor.updateDocumentSettings({
              meta: {
                ...doc.meta,
                recentStrokeColors: [
                  color,
                  ...recentStrokeColors.filter((c) => c !== color).slice(0, 10),
                ],
              } as LDDocumentMeta,
            })
          }}
          onMarkerRadiusChange={onMarkerRadiusChange}
          onPencilRadiusChange={onPencilRadiusChange}
        />
      </ToolbarPosition>

      <ToolbarPosition
        anchor="left"
        visible={showLayers}
        style={{
          top: 160,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <RoundedToolbar direction="vertical">
          <IconButton
            size="small"
            sx={{ width: 24, height: 24 }}
            onClick={handleAddLayerClick}
          >
            <Plus fontSize="inherit" />
          </IconButton>
        </RoundedToolbar>
        <LayersToolbar2
          onSelectLayer={handleSelectLayer}
          onSelectedLayerClick={() => dispatch(setEditingLayer(!editingLayer))}
        />
      </ToolbarPosition>

      <ToolbarPosition anchor="bottom" visible style={{ left: 20 }}>
        <ToolsToolbar
          onOnlySelectedLayerChange={onOnlySelectedLayerChange}
          onLayerSettingsClick={() => dispatch(setEditingLayer(true))}
        />
      </ToolbarPosition>

      {/* <ToolbarPosition
        anchor="bottom"
        visible={isSelecting}
        style={{ left: 20, right: 20, zIndex: 101 }}
      >
        <SelectionToolbar />
      </ToolbarPosition> */}

      <ToolbarPosition
        anchor="right"
        visible={isSelecting && selectedShapes.length > 0}
        style={{ top: 160 }}
      >
        {isSelecting && selectedShapes.length > 0 && (
          <StickerParametersToolbar stickers={selectedShapes} />
        )}
      </ToolbarPosition>

      <PageSettingsDialog2
        open={pageSettingsDialogOpen}
        onClose={() => {
          ////console.log("CLOSING")
          dispatch(hidePageSettingsDialog())
          // //console.log("SETTING STATE")
        }}
      />

      {/* Todo(Alex): figure out how to use visible property without causing blocking overlay */}
      {editingLayer && (
        <ToolbarPosition
          anchor="left"
          visible={editingLayer}
          style={{ bottom: 80 }}
        >
          <LayerOverridesToolbar />
        </ToolbarPosition>
      )}
    </>
  )
}
